import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DndContext,
  DragEndEvent,
  DragMoveEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { StatementEditorV2ContentDataType, StatementEditorV2IndexNode } from '../../types'
import {
  ActiveDropTarget,
  collectContentRefsFromIndexNodes,
  collectExpandedNodeIds,
  deleteIndexNode,
  denormalizeIndexData,
  generateContentId,
  getDropPosition,
  getItemByPath,
  getResolvedDropTarget,
  getRemovedContentRefIds as computeRemovedContentRefIds,
  isValidIndexDropTarget,
  linkContentRefToIndexNode,
  moveIndexNode,
  normalizeIndexData,
  parseIndexPath,
  ReorderableIndexNode,
} from './indexReorderUtils'
import { IndexReorderDragOverlay, IndexReorderSelector } from './IndexReorderSelector'

export function useIndexReorder(
  initialIndexData: StatementEditorV2IndexNode[],
  existingContentIds: string[],
  enableAddContent = false
) {
  const originalNodes = useMemo(() => normalizeIndexData(initialIndexData), [initialIndexData])
  const [isReorderMode, setIsReorderMode] = useState(false)
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false)
  const [isAddContentSheetOpen, setIsAddContentSheetOpen] = useState(false)
  const [addContentTargetNodeId, setAddContentTargetNodeId] = useState<string | null>(null)
  const [localNodes, setLocalNodes] = useState<ReorderableIndexNode[]>(originalNodes)
  const [pendingContentItems, setPendingContentItems] = useState<StatementEditorV2ContentDataType[]>([])
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [activeDropTarget, setActiveDropTarget] = useState<ActiveDropTarget>(null)
  const [draggedNode, setDraggedNode] = useState<ReorderableIndexNode | null>(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const resetPendingState = useCallback(() => {
    setPendingContentItems([])
    setAddContentTargetNodeId(null)
    setIsAddContentSheetOpen(false)
  }, [])

  useEffect(() => {
    if (!isReorderMode) {
      setLocalNodes(originalNodes)
    }
  }, [originalNodes, isReorderMode])

  const enterReorderMode = useCallback(() => {
    setLocalNodes(originalNodes)
    setExpandedItems(collectExpandedNodeIds(originalNodes))
    setIsCreateSheetOpen(false)
    resetPendingState()
    setIsReorderMode(true)
  }, [originalNodes, resetPendingState])

  const cancelReorder = useCallback(() => {
    setLocalNodes(originalNodes)
    setExpandedItems([])
    setActiveDropTarget(null)
    setDraggedNode(null)
    setIsCreateSheetOpen(false)
    resetPendingState()
    setIsReorderMode(false)
  }, [originalNodes, resetPendingState])

  const exitReorderMode = useCallback(() => {
    setExpandedItems([])
    setActiveDropTarget(null)
    setDraggedNode(null)
    setIsCreateSheetOpen(false)
    resetPendingState()
    setIsReorderMode(false)
  }, [resetPendingState])

  const toggleExpand = useCallback((id: string) => {
    setExpandedItems((prev) => (prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]))
  }, [])

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setDraggedNode(getItemByPath(localNodes, parseIndexPath(String(event.active.id))))
    },
    [localNodes]
  )

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      const overId = event.over?.id ? String(event.over.id) : null
      if (!overId) {
        setActiveDropTarget(null)
        return
      }

      const position = getDropPosition(event)
      const dropTarget = position ? getResolvedDropTarget(localNodes, expandedItems, overId, position) : null
      if (dropTarget && isValidIndexDropTarget(localNodes, dropTarget)) {
        setActiveDropTarget(dropTarget)
      } else {
        setActiveDropTarget(null)
      }
    },
    [expandedItems, localNodes]
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const sourceId = String(event.active.id)
      const overId = event.over?.id ? String(event.over.id) : null
      setActiveDropTarget(null)
      setDraggedNode(null)

      if (!overId) {
        return
      }

      const position = getDropPosition(event)
      if (!position) {
        return
      }

      const dropTarget = getResolvedDropTarget(localNodes, expandedItems, overId, position)
      if (!isValidIndexDropTarget(localNodes, dropTarget)) {
        return
      }

      const result = moveIndexNode(localNodes, sourceId, dropTarget, expandedItems)
      setLocalNodes(result.nodes)
      setExpandedItems(result.expandedItems)
    },
    [expandedItems, localNodes]
  )

  const handleDragCancel = useCallback(() => {
    setActiveDropTarget(null)
    setDraggedNode(null)
  }, [])

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      const result = deleteIndexNode(localNodes, nodeId, expandedItems)
      setLocalNodes(result.nodes)
      setExpandedItems(result.expandedItems)
      setActiveDropTarget(null)
      setDraggedNode(null)
    },
    [expandedItems, localNodes]
  )

  const handleAddNode = useCallback((node: ReorderableIndexNode) => {
    setLocalNodes((prev) => [...prev, node])
  }, [])

  const handleOpenAddContent = useCallback((nodeId: string) => {
    setAddContentTargetNodeId(nodeId)
    setIsAddContentSheetOpen(true)
  }, [])

  const handleAddContentSheetOpenChange = useCallback((open: boolean) => {
    setIsAddContentSheetOpen(open)
    if (!open) {
      setAddContentTargetNodeId(null)
    }
  }, [])

  const handleAddContent = useCallback(
    (contentFields: Omit<StatementEditorV2ContentDataType, 'id'>): string | undefined => {
      if (!addContentTargetNodeId) {
        return undefined
      }

      const knownContentIds = [...existingContentIds, ...pendingContentItems.map((item) => item.id)]
      const contentItem: StatementEditorV2ContentDataType = {
        id: generateContentId(knownContentIds),
        ...contentFields,
      }

      setPendingContentItems((prev) => [...prev, contentItem])
      setLocalNodes((prev) => linkContentRefToIndexNode(prev, addContentTargetNodeId, contentItem.id))
      setAddContentTargetNodeId(null)
      setIsAddContentSheetOpen(false)
      return contentItem.id
    },
    [addContentTargetNodeId, existingContentIds, pendingContentItems]
  )

  const getSerializedIndexData = useCallback(() => {
    return denormalizeIndexData(localNodes)
  }, [localNodes])

  const getRemovedContentRefIds = useCallback(() => {
    return computeRemovedContentRefIds(originalNodes, localNodes)
  }, [localNodes, originalNodes])

  const getNewContentItems = useCallback(() => {
    const refsInTree = collectContentRefsFromIndexNodes(localNodes)
    return pendingContentItems.filter((item) => refsInTree.has(item.id))
  }, [localNodes, pendingContentItems])

  const reorderSelector = isReorderMode ? (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <IndexReorderSelector
        indexData={localNodes}
        expandedItems={expandedItems}
        activeDropTarget={activeDropTarget}
        onToggleExpand={toggleExpand}
        onDelete={handleDeleteNode}
        onAddContent={enableAddContent ? handleOpenAddContent : undefined}
        canAddContent={enableAddContent}
      />
      <DragOverlay dropAnimation={null}>
        {draggedNode ? <IndexReorderDragOverlay node={draggedNode} /> : null}
      </DragOverlay>
    </DndContext>
  ) : null

  return {
    isReorderMode,
    isCreateSheetOpen,
    setIsCreateSheetOpen,
    isAddContentSheetOpen,
    setIsAddContentSheetOpen: handleAddContentSheetOpenChange,
    enterReorderMode,
    cancelReorder,
    exitReorderMode,
    getSerializedIndexData,
    getRemovedContentRefIds,
    getNewContentItems,
    pendingContentItems,
    handleAddNode,
    handleAddContent,
    reorderSelector,
  }
}
